import {
  Box,
  Button,
  Chip,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext, SubscriptionTab } from "contexts/Subscription";
import { useContext } from "react";
import { SubscriptionType } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import { useSubscription } from "utils/useSubscription";

import PlansPrice from "../SharedComponents/Price";
import VATerms from "../SharedComponents/VATerms";
import {
  PlanAction,
  getCurrentPlanButton,
  getPlanLimitText,
  isCurrentPlan,
} from "../helpers";
import styles from "./styles";

interface Props {
  plan: Plans;
}

const FEATURES = [
  {
    icon: "fa-solid fa-bell",
    title: "Smart Notifications",
    description:
      "Email alerts when brands need review or automations need attention",
    iconBg: "#b1b3f8",
    borderColor: "rgba(177,179,248,0.3)",
    background:
      "linear-gradient(150deg, rgba(177, 179, 248, 0.1) 0%, rgba(177, 179, 248, 0.2) 100%)",
  },
  {
    icon: "fa-solid fa-clock",
    title: "Smart Timing",
    description:
      "Auto-send during business hours, avoid weekends for max engagement",
    iconBg: "#f7ee6f",
    borderColor: "rgba(247,238,111,0.4)",
    background:
      "linear-gradient(150deg, rgba(247, 238, 111, 0.1) 0%, rgba(247, 238, 111, 0.2) 100%)",
  },
  {
    icon: "fa-solid fa-bullseye",
    title: "Brand Alignment",
    description:
      "Location alignment & exclude misaligned categories automatically",
    iconBg: "#00a63e",
    iconColor: "white",
    borderColor: "#b9f8cf",
    background:
      "linear-gradient(150deg, rgb(240, 253, 244) 0%, rgba(220, 252, 231, 0.5) 100%)",
  },
];

const STATS = [
  { value: "10x", label: "More Efficient" },
  { value: "24/7", label: "Always Working" },
  { value: "100%", label: "Hands-Free" },
];

const AutopilotPlanCard = ({ plan }: Props) => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { plans, emailSents, subscription, tab } =
    useContext(SubscriptionContext);
  const { onPlanButtonClick } = useSubscription();
  const isMediumScreen = useMediaQuery("(max-width:1450px)");

  const getGrid = () => {
    if (isMediumScreen) {
      return 6;
    }
    return 4;
  };

  const planName = plans?.[plan]?.display_name;
  const action = getCurrentPlanButton(
    plan,
    subscription?.subscription?.planName || Plans.FREE,
    subscription,
  );
  const totalEmails = plans?.[plan]?.monthly_limit || 0;
  const BENEFITS = [
    {
      icon: "🚀",
      primary: (
        <>
          Send <strong>{totalEmails} emails</strong> per month
        </>
      ),
    },
    {
      icon: "🤖",
      primary: (
        <>
          Input your preferences for <strong>smart sourcing brands</strong> from
          sources like Instagram, TikTok, Google Maps & more
        </>
      ),
    },
    {
      icon: "✍️",
      primary: (
        <>
          <strong>Runs on autopilot</strong> so you set it up once and have
          weekly pitches sent automatically
        </>
      ),
    },
    {
      icon: "🎯",
      primary: (
        <>
          <strong>Full control</strong> to review, approve, or pause any time
        </>
      ),
    },
    {
      icon: "💸",
      primary: (
        <>
          Exclusive <strong>Brand Pay Data</strong>
        </>
      ),
    },
  ];

  const _isCurrentPlan =
    isCurrentPlan(subscription, plan) &&
    (subscription?.subscription?.status === SubscriptionType.ACTIVE ||
      subscription?.subscription?.status === SubscriptionType.PAST_DUE);

  return (
    <>
      <Box sx={styles.card}>
        <Box
          sx={[
            styles.planTitleCard,
            {
              background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
            },
          ]}
        >
          <Grid container justifyContent="flex-end">
            <Chip
              sx={{ backgroundColor: "purple.main", px: 1 }}
              label={
                <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
                  <strong>
                    <Box component="span" className="fa-solid fa-crown" />{" "}
                    Hands-off pitching for busy creators{" "}
                  </strong>
                </Typography>
              }
            />
          </Grid>

          <Typography variant="h3" sx={{ mt: 6, color: "white" }}>
            {planName}{" "}
            {_isCurrentPlan && (
              <Box component="span" sx={{ color: "#f59e0b", fontSize: 16 }}>
                (Current Plan)
              </Box>
            )}
          </Typography>
          <Box sx={styles.price}>
            {tab === SubscriptionTab.monthly && (
              <PlansPrice plan={Plans.AUTOPILOT} />
            )}
            {tab === SubscriptionTab.yearly && (
              <PlansPrice plan={Plans.AUTOPILOT_YEARLY} />
            )}
          </Box>

          <Grid item xs={12}>
            <VATerms plan={plan} />
          </Grid>
        </Box>

        <Typography sx={{ mb: 2 }}>
          <strong>Everything in Pro Plan PLUS:</strong>
        </Typography>

        {BENEFITS.map((benefit, index) => (
          <Grid container key={index} alignItems="center" sx={{ mb: 1.5 }}>
            <Grid
              container
              justifyContent="left"
              alignItems="center"
              item
              xs="auto"
            >
              <Box sx={styles.iconBlock}>{benefit.icon}</Box>
            </Grid>
            <Grid item xs md>
              <Typography component="div">{benefit.primary}</Typography>
            </Grid>
          </Grid>
        ))}

        <Grid container spacing={1} sx={{ mb: 2 }}>
          {FEATURES.map((feature, index) => (
            <Grid
              item
              xs={12}
              sm={4}
              md={getGrid()}
              key={index}
              sx={{ display: "flex" }}
            >
              <Box
                sx={{
                  p: 2,
                  borderRadius: "14px",
                  border: `1px solid ${feature.borderColor}`,
                  background: feature.background,
                  flex: 1,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    backgroundColor: feature.iconBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.5,
                  }}
                >
                  <Box
                    component="span"
                    className={feature.icon}
                    sx={{
                      fontSize: 16,
                      color: feature.iconColor || "#014339",
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#014339",
                    lineHeight: "20px",
                    mb: 0.5,
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "rgba(1,67,57,0.7)",
                    lineHeight: "19.5px",
                  }}
                >
                  {feature.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Grid
          container
          justifyContent="space-around"
          sx={{
            backgroundColor: "#f8fafc",
            borderRadius: 2,
            py: 1.5,
            mb: 2,
          }}
        >
          {STATS.map((stat, index) => (
            <Grid item key={index} sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontWeight: 800, fontSize: 18, color: "#4338ca" }}
              >
                {stat.value}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                {stat.label}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {!currentOrg?.overridePlanName && (
          <Button
            disableElevation
            fullWidth
            variant={action === PlanAction.upgrade ? "contained" : "text"}
            sx={{
              mt: 1,
              py: 1,
              borderRadius: 2,
              backgroundColor:
                action === PlanAction.upgrade ? "#4338ca" : "#f4f8f6",
              "&:hover": {
                backgroundColor:
                  action === PlanAction.upgrade ? "#3730a3" : "#e8ece9",
              },
            }}
            onClick={() => onPlanButtonClick(plan, action)}
          >
            {action}{" "}
            {tab === SubscriptionTab.yearly &&
            (action === PlanAction.upgrade || action === PlanAction.downgrade)
              ? "+ Get Free Template"
              : ""}
          </Button>
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        {getPlanLimitText(subscription, plan, emailSents, plans)}
      </Box>
    </>
  );
};

export default AutopilotPlanCard;
